import * as THREE from "three"

export const createViewerCameraController = ({
  state,
  camera,
  perspectiveCamera,
  controls,
  setActiveCamera,
  applyOrbitBounds,
  setActiveButton,
  notifyEmbedStateChanged,
  vectorFromPosition,
  clampNumber
}) => {
  const cancelCameraAnimation = () => {
    state.cameraAnimation = null
  }

  const walkthroughDirectionVector = () => {
    const yaw = THREE.MathUtils.degToRad(state.viewerSettings.walkthroughYawDegrees)
    const pitch = THREE.MathUtils.degToRad(clampNumber(state.viewerSettings.walkthroughPitchDegrees, -80, 80))
    const horizontal = Math.cos(pitch)

    return new THREE.Vector3(
      -Math.cos(yaw) * horizontal,
      Math.sin(pitch),
      -Math.sin(yaw) * horizontal
    ).normalize()
  }

  const focusCameraOnPoint = (point, radius = 1) => {
    if (!point || !state.framing) return

    setActiveCamera(perspectiveCamera)
    state.viewMode = "3d"
    state.autoRotate = false
    controls.autoRotate = false

    const offset = camera.position.clone().sub(controls.target)
    if (offset.lengthSq() < 0.001) offset.set(1, 0.7, 1)
    if (state.orbitLock && offset.y < 0.25) offset.y = 0.45
    offset.normalize()

    const framingRadius = Math.max(state.framing.sphere?.radius || 1, 1)
    const distance = Math.max(radius * 4.5, framingRadius * 0.2, 2.5)

    camera.position.copy(point.clone().add(offset.multiplyScalar(distance)))
    controls.target.copy(point)
    camera.near = Math.max(distance / 100, 0.1)
    camera.far = Math.max(camera.far, distance * 120)
    camera.updateProjectionMatrix()
    applyOrbitBounds()
    setActiveButton()
    notifyEmbedStateChanged()
  }

  const animateWalkthroughToNavigationPoint = (navigationPoint) => {
    if (!navigationPoint || !state.framing) return

    cancelCameraAnimation()
    const previousMode = state.viewMode
    const currentCamera = camera
    const fromCamera = currentCamera.position.clone()
    const fromTarget = controls.target.clone()

    setActiveCamera(perspectiveCamera)
    state.viewMode = "walkthrough"
    state.autoRotate = false
    controls.autoRotate = false

    const destination = vectorFromPosition(navigationPoint.position)
    perspectiveCamera.position.copy(fromCamera)
    controls.target.copy(fromTarget)

    const floorY = state.framing.box.min.y
    const defaultEyeHeight = Math.max(state.viewerSettings.walkthroughEyeHeightRatio, 0.2)
    let nextCameraPosition
    let nextTargetPosition

    if (previousMode === "walkthrough") {
      let currentDirection = fromTarget.clone().sub(fromCamera)
      if (currentDirection.lengthSq() < 0.0001) currentDirection = walkthroughDirectionVector()
      currentDirection.normalize()

      const eyeHeight = Math.max(fromCamera.y - floorY, defaultEyeHeight)
      nextCameraPosition = new THREE.Vector3(destination.x, floorY + eyeHeight, destination.z)
      nextTargetPosition = nextCameraPosition.clone().add(currentDirection)
    } else {
      const yaw = THREE.MathUtils.degToRad(state.viewerSettings.walkthroughYawDegrees)
      const pitch = THREE.MathUtils.degToRad(clampNumber(state.viewerSettings.walkthroughPitchDegrees, -80, 80))
      const depthOffset = Math.max(state.viewerSettings.walkthroughZoomMultiplier, 0.1)
      const cameraY = floorY + defaultEyeHeight
      const targetY = cameraY + Math.tan(pitch) * depthOffset

      nextCameraPosition = new THREE.Vector3(
        destination.x + Math.cos(yaw) * depthOffset,
        cameraY,
        destination.z + Math.sin(yaw) * depthOffset
      )
      nextTargetPosition = new THREE.Vector3(destination.x, targetY, destination.z)
    }

    const horizontalDistance = Math.max(
      Math.hypot(nextCameraPosition.x - nextTargetPosition.x, nextCameraPosition.z - nextTargetPosition.z),
      0.1
    )

    camera.near = Math.max(horizontalDistance / 100, 0.1)
    camera.far = Math.max(horizontalDistance * 200, 200)
    camera.updateProjectionMatrix()
    applyOrbitBounds({ mode: "walkthrough" })
    setActiveButton()
    notifyEmbedStateChanged()

    state.cameraAnimation = {
      startedAt: performance.now(),
      durationMs: 850,
      fromCamera,
      toCamera: nextCameraPosition,
      fromTarget,
      toTarget: nextTargetPosition
    }
  }

  const focusOnNodeObject = (object, point = null) => {
    if (!object) return

    const box = new THREE.Box3().setFromObject(object)
    const sphere = box.getBoundingSphere(new THREE.Sphere())
    focusCameraOnPoint(point ? point.clone() : sphere.center, Math.max(sphere.radius, 0.8))
  }

  const focusOnPlacement = (placement) => {
    if (!placement) return

    const object = state.placementObjectsById.get(placement.id)
    if (object) {
      const box = new THREE.Box3().setFromObject(object)
      const sphere = box.getBoundingSphere(new THREE.Sphere())
      focusCameraOnPoint(sphere.center, Math.max(sphere.radius, (state.framing?.sphere?.radius || 1) * 0.24))
      return
    }

    focusCameraOnPoint(vectorFromPosition(placement.render_position || placement.position), Math.max((state.framing?.sphere?.radius || 1) * 0.24, 4.5))
  }

  const focusOnNavigationPoint = (navigationPoint) => {
    if (!navigationPoint) return
    animateWalkthroughToNavigationPoint(navigationPoint)
  }

  return {
    cancelCameraAnimation,
    focusCameraOnPoint,
    animateWalkthroughToNavigationPoint,
    focusOnNodeObject,
    focusOnPlacement,
    focusOnNavigationPoint
  }
}
